import { getMaxAuthPayload } from "../services/init-data.service";

// API base URL — configure via VITE_API_URL env var or change default
const API_BASE_URL: string =
  import.meta.env.VITE_API_URL ?? "https://api.cryptcloud.example.com";

// API path prefix — configure via VITE_API_PATH env var
export const API_PATH: string = import.meta.env.VITE_API_PATH ?? "/api";

// ---------------------------------------------------------------------------
// Test mode support — when __TEST_MODE__ global flag is set, all API calls
// are routed through mock handlers instead of making real HTTP requests.
// ---------------------------------------------------------------------------
function isTestMode(): boolean {
  const active =
    typeof globalThis !== "undefined" &&
    (globalThis as Record<string, unknown>).__TEST_MODE__ === true;
  if (active) {
    console.log(
      "[API CLIENT] 🔬 Test mode active — routing through mock handlers",
    );
  }
  return active;
}

// ---------------------------------------------------------------------------
// Auto re-authentication — when the server returns 401 (access token expired,
// max 15 min), we re-login with the raw initData from Max Messenger and retry
// the original request once. This avoids forcing the user to reopen the app.
// ---------------------------------------------------------------------------
let reauthInProgress: Promise<boolean> | null = null;

/** Persists tokens to localStorage and keeps the Zustand store in sync. */
async function persistTokens(result: {
  accessToken: string;
  refreshToken?: string;
  instanceId?: number;
}): Promise<void> {
  localStorage.setItem("access_token", result.accessToken);
  localStorage.setItem("refresh_token", result.refreshToken ?? "");
  localStorage.setItem("instance_id", String(result.instanceId ?? ""));

  const { useAuthStore } = await import("../store/auth.store");
  useAuthStore
    .getState()
    .setTokens(
      result.accessToken,
      result.refreshToken ?? "",
      result.instanceId ?? 0,
    );
}

/**
 * Refreshes tokens via POST /api/auth/refresh using the stored refresh_token.
 * Returns true when new tokens were stored successfully.
 */
async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    console.warn("[API CLIENT] Refresh skipped: no refresh_token in storage");
    return false;
  }

  try {
    // Direct fetch without an Authorization header — an expired access token
    // would be rejected by the backend JWT filter before reaching the controller.
    const response = await fetch(`${API_BASE_URL}${API_PATH}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      console.warn(
        "[API CLIENT] Refresh failed: refresh returned",
        response.status,
      );
      return false;
    }

    const result = await response.json();
    if (!result?.accessToken) {
      console.warn("[API CLIENT] Refresh failed: no accessToken in response");
      return false;
    }

    await persistTokens(result);
    console.log("[API CLIENT] Tokens refreshed successfully");
    return true;
  } catch (err) {
    console.warn("[API CLIENT] Refresh error:", err);
    return false;
  }
}

/**
 * Re-authenticates the MiniApp user. Priority:
 * 1. MAX Messenger initData login (POST /api/minapp/login);
 * 2. fallback — refresh_token rotation (POST /api/auth/refresh).
 * Returns true when new tokens were stored successfully.
 */
async function tryReauthenticate(): Promise<boolean> {
  if (reauthInProgress) return reauthInProgress;

  reauthInProgress = (async () => {
    try {
      const initData = getMaxAuthPayload()?.initData;
      if (initData) {
        // Direct fetch — deliberately bypasses apiFetch to avoid recursion
        // (login endpoint could itself return 401 and re-trigger this logic).
        const loginResponse = await fetch(
          `${API_BASE_URL}${API_PATH}/minapp/login`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ initData }),
          },
        );

        if (loginResponse.ok) {
          const result = await loginResponse.json();
          if (result?.accessToken) {
            await persistTokens(result);
            console.log("[API CLIENT] Re-authenticated successfully");
            return true;
          }
          console.warn(
            "[API CLIENT] Re-auth failed: no accessToken in response",
          );
        } else {
          console.warn(
            "[API CLIENT] Re-auth failed: login returned",
            loginResponse.status,
          );
        }
      } else {
        console.warn("[API CLIENT] Re-auth skipped: no initData in WebApp");
      }

      // Fallback: restore the session via refresh_token rotation.
      return await tryRefreshToken();
    } catch (err) {
      console.warn("[API CLIENT] Re-auth error:", err);
      return false;
    } finally {
      reauthInProgress = null;
    }
  })();

  return reauthInProgress;
}

function getAuthHeaders(): HeadersInit {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorBody = await response.text();
      if (errorBody) {
        const parsed = JSON.parse(errorBody);
        errorMessage =
          parsed.message || parsed.error || parsed.detail || errorMessage;
      }
    } catch {
      // ignore parse error, use default error message
    }

    // Provide user-friendly messages for common HTTP status codes.
    // For 401 keep the server-provided reason (e.g. "Token expired",
    // "Invalid token") when available — it is far more actionable than the
    // generic fallback and critical for diagnosing auth issues.
    if (response.status === 401) {
      errorMessage =
        errorMessage !== `HTTP ${response.status}`
          ? errorMessage
          : "Unauthorized. Please log in again.";
    } else if (response.status === 403) {
      errorMessage = "Access denied. You do not have permission.";
    } else if (response.status === 404) {
      errorMessage =
        errorMessage !== `HTTP ${response.status}`
          ? errorMessage
          : "Resource not found.";
    } else if (response.status === 409) {
      errorMessage =
        errorMessage !== `HTTP ${response.status}`
          ? errorMessage
          : "Conflict. The resource may already exist.";
    } else if (response.status >= 500) {
      errorMessage = "Server error. Please try again later.";
    }

    throw new ApiError(errorMessage, response.status);
  }
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function apiFetch<T>(
  url: string,
  options: RequestInit,
  retried = false,
): Promise<T> {
  try {
    const response = await fetch(url, options);
    return handleResponse<T>(response);
  } catch (error) {
    // 401 Unauthorized — access token expired. Try to re-authenticate once
    // and retry the original request before surfacing the error to the user.
    if (error instanceof ApiError && error.status === 401 && !retried) {
      const reauthenticated = await tryReauthenticate();
      if (reauthenticated) {
        return apiFetch<T>(
          url,
          { ...options, headers: getAuthHeaders() },
          true,
        );
      }
    }

    // Handle network errors (e.g., DNS failure, connection refused)
    if (error instanceof TypeError) {
      throw new Error(
        "Network error. Please check your connection and try again.",
      );
    }
    throw error;
  }
}

/** Error carrying the HTTP status code for retry logic. */
class ApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  // Test mode: delegate to mock handlers
  if (isTestMode()) {
    const { mockApiGet } = await import("./mock-handlers");
    return mockApiGet<T>(path, params);
  }

  let url = `${API_BASE_URL}${path}`;
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  return apiFetch<T>(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  // Test mode: delegate to mock handlers
  if (isTestMode()) {
    const { mockApiPost } = await import("./mock-handlers");
    return mockApiPost<T>(path, body);
  }

  return apiFetch<T>(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  // Test mode: delegate to mock handlers
  if (isTestMode()) {
    const { mockApiPut } = await import("./mock-handlers");
    return mockApiPut<T>(path, body);
  }

  return apiFetch<T>(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiDelete<T>(path: string): Promise<T> {
  // Test mode: delegate to mock handlers
  if (isTestMode()) {
    const { mockApiDelete } = await import("./mock-handlers");
    return mockApiDelete<T>(path);
  }

  return apiFetch<T>(`${API_BASE_URL}${path}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
}
